import { FileIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import ConfirmationModal from '@components/ConfirmationModal'
import FileViewer from '@components/FileViewer/FileViewer'
import { getFileExtension, isViewableImage, openFileInNewTab } from '@components/FileViewer/fileHelpers'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useDeleteFile, useFiles, useTrip, useUploadFiles } from '@hooks'
import { INLINE_IMAGE_MIME_TYPES, isPdfMime } from '@lib/mime'
import { GetFileType } from '@orbit/api-types'
import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import { FileInput } from '@components/Field'
import {
	DeleteFileButton,
	FileCard,
	FileGrid,
	FileInfo,
	FileName,
	FileThumbnail,
	FileTypeIcon,
	UploadButton,
	UploadOverlay,
	UploadText,
} from './FilesPage.styles'
import Spinner from '@components/Spinner'
import { white } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'

const ACCEPTED_TYPES = [
	...INLINE_IMAGE_MIME_TYPES,
	// Uploadable but not browser-renderable — shows as a plain file card.
	'image/heic',
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'text/plain',
	'text/csv',
].join(',')

const MAX_FILES = 5

const FilesPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: filesData, isLoading: filesLoading } = useFiles(tripId)
	const uploadFiles = useUploadFiles()
	const deleteFile = useDeleteFile()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const [viewerFile, setViewerFile] = useState<GetFileType | null>(null)
	const [deleteTarget, setDeleteTarget] = useState<GetFileType | null>(null)

	useBreadcrumbs(
		trip
			? [
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Files' },
				]
			: [],
	)

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected || selected.length === 0 || !tripId) return

		const fileArray = Array.from(selected)

		if (fileArray.length > MAX_FILES) {
			toast.error(`You can upload up to ${MAX_FILES} files at a time.`)
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}

		uploadFiles.mutate(
			{ tripId, files: fileArray, resourceType: 'TRIP' },
			{
				onSuccess: () => {
					toast.success(`${fileArray.length} file${fileArray.length > 1 ? 's' : ''} uploaded`)
				},
				onError: () => {
					toast.error('Failed to upload files. Please try again.')
				},
				onSettled: () => {
					if (fileInputRef.current) fileInputRef.current.value = ''
				},
			},
		)
	}

	const handleFileClick = (file: GetFileType) => {
		if (isViewableImage(file)) {
			setViewerFile(file)
		} else {
			openFileInNewTab(file.url)
		}
	}

	const handleDeleteClick = (e: React.MouseEvent, file: GetFileType) => {
		e.stopPropagation()
		setDeleteTarget(file)
	}

	const handleConfirmDelete = () => {
		if (!deleteTarget || !tripId) return
		deleteFile.mutate(
			{ tripId, fileId: deleteTarget.id },
			{
				onSuccess: () => toast.success('File deleted'),
				onError: () => toast.error('Failed to delete file'),
			},
		)
	}

	if (tripLoading || filesLoading) {
		return (
			<PageLoader label="Loading files..." />
		)
	}

	if (!trip) {
		return (
			<ErrorNote>Trip not found</ErrorNote>
		)
	}

	const files = filesData?.files || []

	return (
		<PageScroll>
			<FileInput
				ref={fileInputRef}
				multiple
				accept={ACCEPTED_TYPES}
				onChange={handleFileChange}
			/>

			{files.length === 0 ? (
				<EmptyState
					art="files"
					title="No files yet"
					description="Photos and documents attached to flights, accommodations, expenses, and more will show up here."
				>
					<EmptyStateButton onClick={handleUploadClick} disabled={uploadFiles.isPending}>
						Upload Files
					</EmptyStateButton>
				</EmptyState>
			) : (
				<PageColumn>
					<PageHeader
						title="Files"
						subtitle={countLabel(files.length, 'file')}
						actions={
							<UploadButton onClick={handleUploadClick} disabled={uploadFiles.isPending}>
								+ Upload
							</UploadButton>
						}
					/>

					<FileGrid>
						{files.map(file => (
							<FileCard key={file.id} onClick={() => handleFileClick(file)}>
								<FileThumbnail $pdf={isPdfMime(file.mimeType)}>
									{isViewableImage(file) ? (
										<img src={file.url} alt={file.fileName} loading="lazy" />
									) : (
										<FileTypeIcon $pdf={isPdfMime(file.mimeType)}>
											<FileIcon sx={{ fontSize: iconSizes.hero }} />
											<span>{getFileExtension(file.fileName)}</span>
										</FileTypeIcon>
									)}
								</FileThumbnail>
								<FileInfo>
									<FileName title={file.fileName}>{file.fileName}</FileName>
									<DeleteFileButton
										onClick={e => handleDeleteClick(e, file)}
										title="Delete file"
									>
										✕
									</DeleteFileButton>
								</FileInfo>
							</FileCard>
						))}
					</FileGrid>
				</PageColumn>
			)}

			{uploadFiles.isPending && (
				<UploadOverlay>
					<Spinner size={48} color={white} trackColor={`${white}4d`} />
					<UploadText>Uploading files...</UploadText>
				</UploadOverlay>
			)}

			<FileViewer
				file={viewerFile}
				onClose={() => setViewerFile(null)}
				onDownloadError={() => toast.error('Failed to download file')}
			/>

			<ConfirmationModal
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleConfirmDelete}
				title="Delete File"
				message={
					<>
						Are you sure you want to delete <strong>{deleteTarget?.fileName}</strong>?
						This cannot be undone.
					</>
				}
				confirmText="Delete"
				variant="danger"
			/>
		</PageScroll>
	)
}

export default FilesPage
